import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  List, 
  ListItem, 
  ListItemText, 
  Button, 
  TextField, 
  Grid, 
  CircularProgress, 
  Alert, 
  Snackbar,
  Switch,
  FormControlLabel,
  Divider,
  Chip,
  Tabs,
  Tab,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton
} from '@mui/material';
import { 
  LocationOn, 
  IntegrationInstructions,
  Palette,
  CreditCard,
  Language,
  Add,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CloudUpload as UploadIcon
} from '@mui/icons-material';
import { useLocation } from '../../contexts/LocationContext';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import ThemeSettings from './ThemeSettings';
import BillingSettings from './BillingSettings';

const SettingsPage = () => {
  const { locations, addLocation, loading, error, clearError, loadLocations } = useLocation();
  const { user } = useAuth();
  const [newLocationName, setNewLocationName] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [pricingLoading, setPricingLoading] = useState(false);
  const [taxRate, setTaxRate] = useState('');
  const [shipping, setShipping] = useState([]);
  const [warranties, setWarranties] = useState([]);
  
  // Integration settings state
  const [integrations, setIntegrations] = useState({
    twilio: { enabled: false, configured: false },
    mailchimp: { enabled: false, configured: false }
  });
  const [integrationsLoading, setIntegrationsLoading] = useState(false);
  
  // External catalogs state
  const [externalCatalogs, setExternalCatalogs] = useState([]);
  const [catalogsLoading, setCatalogsLoading] = useState(false);
  const [newCatalogName, setNewCatalogName] = useState('');
  const [newCatalogUrl, setNewCatalogUrl] = useState('');
  const [newCatalogDescription, setNewCatalogDescription] = useState('');
  
  // Integration configuration state
  const [twilioConfig, setTwilioConfig] = useState({
    accountSid: '',
    authToken: '',
    phoneNumber: ''
  });
  
  const [mailchimpConfig, setMailchimpConfig] = useState({
    apiKey: '',
    serverPrefix: '',
    listId: ''
  });

  const [gmailConfig, setGmailConfig] = useState({
    user: '',
    appPassword: '',
    fromName: ''
  });

  // Domains (DymenWeb) settings state (user-level preferences)
  const [domainLoading, setDomainLoading] = useState(false);
  const [domainSaving, setDomainSaving] = useState(false);
  // Store just the core name (e.g., "tallahassee")
  const [domainCore, setDomainCore] = useState('');
  // Store search URL template for web scraping
  const [searchUrlTemplate, setSearchUrlTemplate] = useState('');
  const [originalPreferences, setOriginalPreferences] = useState(null);

  // Location editing state
  const [editingLocation, setEditingLocation] = useState(null);
  const [locationForm, setLocationForm] = useState({
    name: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    email: '',
    logo: ''
  });
  const [locationSaving, setLocationSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);

  // Load integration settings
  useEffect(() => {
    loadIntegrationSettings();
    loadExternalCatalogs();
  }, []);

  // Ensure locations are loaded for dropdown
  useEffect(() => { loadLocations && loadLocations(); }, []);

  // When locations load, preselect first and fetch pricing
  useEffect(() => {
    if (locations && locations.length > 0) {
      const id = selectedLocationId || (locations[0]._id || locations[0].id);
      if (id && id !== selectedLocationId) {
        setSelectedLocationId(id);
      }
    }
  }, [locations]);

  useEffect(() => {
    if (selectedLocationId) {
      fetchPricing(selectedLocationId);
    }
  }, [selectedLocationId]);

  // Load Domains settings (from user preferences)
  useEffect(() => {
    loadDomainSettings();
  }, []);

  const loadIntegrationSettings = async () => {
    try {
      setIntegrationsLoading(true);
      const response = await api.get('/integrations/settings');
      if (response.data.success) {
        const data = response.data.data;
        setIntegrations(data);
        if (data.gmail?.user) {
          setGmailConfig(prev => ({ ...prev, user: data.gmail.user, fromName: data.gmail.fromName || '' }));
        }
      }
    } catch (error) {
      console.error('Failed to load integration settings:', error);
    } finally {
      setIntegrationsLoading(false);
    }
  };

  const loadDomainSettings = async () => {
    try {
      setDomainLoading(true);
      // Prefer fresh fetch to ensure latest preferences
      const res = await api.get('/users/me');
      const me = res?.data?.data?.user;
      const prefs = me?.preferences || {};
      const dymenweb = prefs.dymenweb || {};
      setOriginalPreferences(prefs);
      // Back-compat: if previewDomain exists, try to infer core from subdomain
      const inferredCore = (() => {
        const pd = dymenweb.previewDomain;
        if (typeof pd === 'string' && pd.length) {
          const host = pd.replace(/^https?:\/\//, '').split('/')[0];
          const first = host.split('.')[0];
          // Skip 'localhost' when format is 'tenant.localhost:3003'
          if (first && first !== 'localhost') return first;
        }
        return '';
      })();
      // Prefer showing the full domain if available; else show domainCore/inferred
      const display = dymenweb.fullDomain || dymenweb.domainCore || inferredCore || '';
      setDomainCore(display);
      // Load search URL template
      setSearchUrlTemplate(dymenweb.searchUrlTemplate || 'https://www.tallahasseediscountfurniture.com/search?q={sku}');
    } catch (e) {
      console.error('Failed to load domain settings:', e);
    } finally {
      setDomainLoading(false);
    }
  };

  // Extract the core subdomain label from input (supports full domains)
  const extractCore = (input) => {
    if (!input) return '';
    const host = String(input)
      .trim()
      .replace(/^https?:\/\//i, '')
      .split('/')[0]
      .toLowerCase();
    if (!host) return '';
    // Handle localhost patterns like core.localhost:3003
    if (host.includes('localhost')) {
      const first = host.split('.')[0];
      return first.split(':')[0];
    }
    const parts = host.split('.').filter(Boolean);
    if (parts.length === 1) return parts[0];
    // Common case: www.brand.tld -> take penultimate
    const penultimate = parts[parts.length - 2];
    if (penultimate === 'www' && parts.length >= 3) return parts[parts.length - 3];
    return penultimate === 'www' ? parts[0] : penultimate;
  };

  const saveDomainSettings = async () => {
    try {
      setDomainSaving(true);
      const full = String(domainCore || '').trim();
      const core = extractCore(full);
      const mergedPrefs = {
        ...(originalPreferences || {}),
        dymenweb: {
          ...(originalPreferences?.dymenweb || {}),
          fullDomain: full,
          domainCore: String(core || '').trim(),
          searchUrlTemplate: searchUrlTemplate.trim()
        }
      };
      const res = await api.put('/users/me', { preferences: mergedPrefs });
      if (res?.data?.success) {
        setOriginalPreferences(mergedPrefs);
        setSuccessMessage('Domain settings saved');
      }
    } catch (e) {
      console.error('Failed to save domain settings:', e);
    } finally {
      setDomainSaving(false);
    }
  };

  const fetchPricing = async (locationId) => {
    try {
      setPricingLoading(true);
      const res = await api.get(`/locations/${locationId}/pricing`);
      if (res.data?.success) {
        const { pricing } = res.data.data || {};
        setTaxRate(
          typeof pricing?.taxRate === 'number'
            ? String(pricing.taxRate)
            : (pricing?.taxRate ? String(pricing.taxRate) : '0')
        );
        setShipping(pricing?.shipping || []);
        setWarranties(pricing?.warranties || []);
      }
    } catch (e) {
      console.error('Failed to load pricing settings:', e);
    } finally {
      setPricingLoading(false);
    }
  };

  const savePricing = async () => {
    if (!selectedLocationId) return;
    try {
      setPricingLoading(true);
      const payload = {
        taxRate: Number(taxRate || 0),
        shipping: shipping.map(s => ({ title: s.title || '', cost: Number(s.cost || 0) })),
        warranties: warranties.map(w => ({ title: w.title || '', cost: Number(w.cost || 0) }))
      };
      const res = await api.put(`/locations/${selectedLocationId}/pricing`, payload);
      if (res.data?.success) {
        setSuccessMessage('Pricing settings saved');
      }
    } catch (e) {
      console.error('Failed to save pricing settings:', e);
    } finally {
      setPricingLoading(false);
    }
  };

  const addItem = (type) => {
    if (type === 'shipping') setShipping(prev => [...prev, { title: '', cost: '' }]);
    if (type === 'warranties') setWarranties(prev => [...prev, { title: '', cost: '' }]);
  };
  const updateItem = (type, idx, key, value) => {
    const setter = type === 'shipping' ? setShipping : setWarranties;
    const list = type === 'shipping' ? shipping : warranties;
    const copy = list.slice();
    copy[idx] = { ...copy[idx], [key]: value };
    setter(copy);
  };
  const removeItem = (type, idx) => {
    const setter = type === 'shipping' ? setShipping : setWarranties;
    const list = type === 'shipping' ? shipping : warranties;
    const copy = list.slice();
    copy.splice(idx, 1);
    setter(copy);
  };

  // Location editing functions
  const handleEditLocation = (location) => {
    setEditingLocation(location);
    setLocationForm({
      name: location.name || '',
      street: location.street || location.address?.street || '',
      city: location.city || location.address?.city || '',
      state: location.state || location.address?.state || '',
      zipCode: location.zipCode || location.address?.zipCode || '',
      phone: location.phone || location.contact?.phone || '',
      email: location.email || location.contact?.email || '',
      logo: location.settings?.logo || location.logo || ''
    });
  };

  const handleCancelEdit = () => {
    setEditingLocation(null);
    setLocationForm({
      name: '',
      street: '',
      city: '',
      state: '',
      zipCode: '',
      phone: '',
      email: '',
      logo: ''
    });
  };

  const handleSaveLocation = async () => {
    if (!editingLocation) return;
    const locationId = editingLocation._id || editingLocation.id;
    
    try {
      setLocationSaving(true);
      const payload = {
        name: locationForm.name,
        address: {
          street: locationForm.street,
          city: locationForm.city,
          state: locationForm.state,
          zipCode: locationForm.zipCode
        },
        contact: {
          phone: locationForm.phone,
          email: locationForm.email
        },
        settings: {
          ...(editingLocation.settings || {}),
          logo: locationForm.logo
        }
      };
      
      const res = await api.put(`/locations/${locationId}`, payload);
      if (res.data?.success) {
        setSuccessMessage('Location updated successfully!');
        setEditingLocation(null);
        loadLocations && loadLocations();
      }
    } catch (e) {
      console.error('Failed to save location:', e);
    } finally {
      setLocationSaving(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      setLogoUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await api.post('/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (res.data?.success && res.data?.data?.url) {
        setLocationForm(prev => ({ ...prev, logo: res.data.data.url }));
        setSuccessMessage('Logo uploaded successfully!');
      }
    } catch (e) {
      console.error('Failed to upload logo:', e);
    } finally {
      setLogoUploading(false);
    }
  };

  const handleAddLocation = async () => {
    if (newLocationName.trim()) {
      // Create a complete location object with all required fields
      const locationData = {
        name: newLocationName,
        type: 'store',  // Must be one of: 'warehouse', 'store', 'showroom', 'office'
        address: {
          street: '123 Main St',
          city: 'Anytown',
          state: 'CA',
          zipCode: '12345',
          country: 'USA'
        },
        contact: {
          phone: '123-456-7890',  // Must match regex pattern
          email: 'contact@example.com'  // Must be valid email
        },
        settings: {
          timezone: 'America/Los_Angeles',  // Must be non-empty
          currency: 'USD'  // Must be one of: 'USD', 'EUR', 'GBP', 'CAD', 'AUD'
        }
      };
      
      const result = await addLocation(locationData);
      if (result.success) {
        setSuccessMessage(`Location "${newLocationName}" created successfully!`);
        setNewLocationName('');
      }
    }
  };
  
  const handleCloseSuccess = () => {
    setSuccessMessage('');
  };
  
  const handleCloseError = () => {
    clearError();
  };

  const handleIntegrationToggle = async (integration) => {
    try {
      setIntegrationsLoading(true);
      const newEnabled = !integrations[integration].enabled;
      
      const response = await api.put(`/integrations/${integration}/toggle`, {
        enabled: newEnabled
      });
      
      if (response.data.success) {
        setIntegrations(prev => ({
          ...prev,
          [integration]: {
            ...prev[integration],
            enabled: newEnabled
          }
        }));
        setSuccessMessage(`${integration.charAt(0).toUpperCase() + integration.slice(1)} integration ${newEnabled ? 'enabled' : 'disabled'} successfully!`);
      }
    } catch (error) {
      console.error(`Failed to toggle ${integration} integration:`, error);
      setSuccessMessage('');
      clearError();
      // Set error through context if available, otherwise use local state
    } finally {
      setIntegrationsLoading(false);
    }
  };

  const handleConfigureIntegration = async (integration, config) => {
    try {
      setIntegrationsLoading(true);
      
      const response = await api.put(`/integrations/${integration}/configure`, {
        config
      });
      
      if (response.data.success) {
        setIntegrations(prev => ({
          ...prev,
          [integration]: {
            ...prev[integration],
            configured: true
          }
        }));
        setSuccessMessage(`${integration.charAt(0).toUpperCase() + integration.slice(1)} integration configured successfully!`);
      }
    } catch (error) {
      console.error(`Failed to configure ${integration} integration:`, error);
      setSuccessMessage('');
      clearError();
    } finally {
      setIntegrationsLoading(false);
    }
  };

  const handleTestIntegration = async (integration) => {
    try {
      setIntegrationsLoading(true);
      
      const response = await api.post(`/integrations/${integration}/test`);
      
      if (response.data.success) {
        setSuccessMessage(response.data.message);
      }
    } catch (error) {
      console.error(`Failed to test ${integration} integration:`, error);
      setSuccessMessage('');
      clearError();
    } finally {
      setIntegrationsLoading(false);
    }
  };

  // External catalog functions
  const loadExternalCatalogs = async () => {
    try {
      setCatalogsLoading(true);
      const response = await api.get('/external-catalogs');
      if (response.data.success) {
        setExternalCatalogs(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load external catalogs:', error);
    } finally {
      setCatalogsLoading(false);
    }
  };

  const handleAddCatalog = async () => {
    if (!newCatalogName.trim() || !newCatalogUrl.trim()) return;
    
    try {
      setCatalogsLoading(true);
      const response = await api.post('/external-catalogs', {
        name: newCatalogName.trim(),
        baseUrl: newCatalogUrl.trim(),
        description: newCatalogDescription.trim(),
        locationId: selectedLocationId
      });
      
      if (response.data.success) {
        setExternalCatalogs(prev => [...prev, response.data.data]);
        setNewCatalogName('');
        setNewCatalogUrl('');
        setNewCatalogDescription('');
        setSuccessMessage(`External catalog "${newCatalogName}" added successfully!`);
      }
    } catch (error) {
      console.error('Failed to add external catalog:', error);
      setSuccessMessage('');
      clearError();
    } finally {
      setCatalogsLoading(false);
    }
  };

  const handleDeleteCatalog = async (catalogId) => {
    try {
      setCatalogsLoading(true);
      const response = await api.delete(`/external-catalogs/${catalogId}`);
      
      if (response.data.success) {
        setExternalCatalogs(prev => prev.filter(cat => cat.id !== catalogId));
        setSuccessMessage('External catalog deleted successfully!');
      }
    } catch (error) {
      console.error('Failed to delete external catalog:', error);
      setSuccessMessage('');
      clearError();
    } finally {
      setCatalogsLoading(false);
    }
  };

  const handleSyncCatalog = async (catalogId) => {
    try {
      setCatalogsLoading(true);
      const response = await api.post(`/external-catalogs/${catalogId}/sync`);
      
      if (response.data.success) {
        setSuccessMessage(`Catalog sync initiated. ${response.data.message || 'Products will be updated in the background.'}`);
        loadExternalCatalogs(); // Refresh the list
      }
    } catch (error) {
      console.error('Failed to sync external catalog:', error);
      setSuccessMessage('');
      clearError();
    } finally {
      setCatalogsLoading(false);
    }
  };

  return (
    <Box>
      <Snackbar open={!!successMessage} autoHideDuration={5000} onClose={handleCloseSuccess}>
        <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
      
      <Snackbar open={!!error} autoHideDuration={5000} onClose={handleCloseError}>
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab icon={<LocationOn />} label="Locations" />
          <Tab icon={<IntegrationInstructions />} label="Integrations" />
          <Tab icon={<Palette />} label="Appearance" />
          <Tab icon={<CreditCard />} label="Billing" />
          <Tab icon={<CreditCard />} label="Pricing" />
          <Tab icon={<Language />} label="Domains" />
        </Tabs>
      </Paper>

      {/* Locations Tab */}
      {activeTab === 0 && (
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Manage Locations</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Click Edit to update location details that appear on invoices
              </Typography>
              
              {loading ? (
                <Box display="flex" justifyContent="center" my={3}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box sx={{ mb: 3 }}>
                  {locations && locations.length > 0 ? (
                    <List>
                      {locations.map(location => (
                        <ListItem 
                          key={location._id || location.id || 'unknown-key'}
                          secondaryAction={
                            <IconButton edge="end" onClick={() => handleEditLocation(location)}>
                              <EditIcon />
                            </IconButton>
                          }
                        >
                          <ListItemText 
                            primary={location.name || 'Unnamed Location'}
                            secondary={
                              <>
                                {location.street || location.address?.street || 'No address'}, {location.city || location.address?.city || ''} {location.state || location.address?.state || ''}
                                <br />
                                {location.phone || location.contact?.phone || 'No phone'} | {location.email || location.contact?.email || 'No email'}
                              </>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Box sx={{ color: 'text.secondary', py: 2 }}>
                      No locations found
                      <br />
                      Create a new location to get started
                    </Box>
                  )}
                </Box>
              )}
            </Paper>
            
            <Paper sx={{ p: 3, mt: 3 }}>
              <Typography variant="h6" gutterBottom>Add New Location</Typography>
              <TextField
                fullWidth
                label="New Location Name"
                value={newLocationName}
                onChange={(e) => setNewLocationName(e.target.value)}
                margin="normal"
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddLocation}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Add Location'}
              </Button>
            </Paper>
          </Grid>
          
          {/* Location Edit Form */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {editingLocation ? `Edit: ${editingLocation.name}` : 'Location Details'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This information appears on invoices as your company details
              </Typography>
              
              {editingLocation ? (
                <>
                  <TextField
                    fullWidth
                    label="Business Name"
                    value={locationForm.name}
                    onChange={(e) => setLocationForm(prev => ({ ...prev, name: e.target.value }))}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Street Address"
                    value={locationForm.street}
                    onChange={(e) => setLocationForm(prev => ({ ...prev, street: e.target.value }))}
                    margin="normal"
                  />
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="City"
                        value={locationForm.city}
                        onChange={(e) => setLocationForm(prev => ({ ...prev, city: e.target.value }))}
                        margin="normal"
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        label="State"
                        value={locationForm.state}
                        onChange={(e) => setLocationForm(prev => ({ ...prev, state: e.target.value }))}
                        margin="normal"
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        label="Zip Code"
                        value={locationForm.zipCode}
                        onChange={(e) => setLocationForm(prev => ({ ...prev, zipCode: e.target.value }))}
                        margin="normal"
                      />
                    </Grid>
                  </Grid>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={locationForm.phone}
                    onChange={(e) => setLocationForm(prev => ({ ...prev, phone: e.target.value }))}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Email"
                    value={locationForm.email}
                    onChange={(e) => setLocationForm(prev => ({ ...prev, email: e.target.value }))}
                    margin="normal"
                  />
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="subtitle1" gutterBottom>Company Logo (for Invoices)</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    {locationForm.logo && (
                      <Box 
                        component="img" 
                        src={locationForm.logo} 
                        alt="Logo" 
                        sx={{ maxWidth: 120, maxHeight: 60, objectFit: 'contain', border: '1px solid #ddd', borderRadius: 1, p: 1 }}
                      />
                    )}
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={logoUploading ? <CircularProgress size={16} /> : <UploadIcon />}
                      disabled={logoUploading}
                    >
                      {logoUploading ? 'Uploading...' : 'Upload Logo'}
                      <input type="file" hidden accept="image/*" onChange={handleLogoUpload} />
                    </Button>
                  </Box>
                  <TextField
                    fullWidth
                    label="Logo URL (or paste URL directly)"
                    value={locationForm.logo}
                    onChange={(e) => setLocationForm(prev => ({ ...prev, logo: e.target.value }))}
                    margin="normal"
                    size="small"
                  />
                  
                  <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSaveLocation}
                      disabled={locationSaving}
                    >
                      {locationSaving ? <CircularProgress size={24} /> : 'Save Changes'}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleCancelEdit}
                    >
                      Cancel
                    </Button>
                  </Box>
                </>
              ) : (
                <Box sx={{ color: 'text.secondary', py: 4, textAlign: 'center' }}>
                  Select a location from the list and click Edit to update its details
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Domains Tab */}
      {activeTab === 5 && (
        <Grid container spacing={4}>
          <Grid item xs={12} md={7.2} sx={{ width: '60%' }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Domains & Web Scraping</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Set your core domain name and configure web scraping search URL for product lookups.
              </Typography>

              {domainLoading ? (
                <Box display="flex" justifyContent="center" my={3}><CircularProgress /></Box>
              ) : (
                <>
                  <Typography variant="subtitle1" gutterBottom>Your Domain</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1, mb: 2 }}>
                    <TextField
                      size="small"
                      label="Your Domain (full domain or core, e.g., tallahasseefurniture.com or tallahassee)"
                      value={domainCore}
                      onChange={(e) => setDomainCore(e.target.value)}
                    />
                  </Box>

                  <Typography variant="subtitle1" gutterBottom>Web Scraping Search URL</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1, mb: 2 }}>
                    <TextField
                      size="small"
                      label="Search URL Template (use {sku} as placeholder)"
                      value={searchUrlTemplate}
                      onChange={(e) => setSearchUrlTemplate(e.target.value)}
                      placeholder="https://www.example.com/search?q={sku}"
                      helperText="Use {sku} where the SKU should be inserted in the URL"
                    />
                  </Box>

                  <Box sx={{ mt: 2 }}>
                    <Button variant="contained" disabled={domainSaving} onClick={saveDomainSettings}>
                      {domainSaving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Save'}
                    </Button>
                  </Box>
                </>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Integrations Tab */}
      {activeTab === 1 && (
        <Grid container spacing={4}>
          <Grid item xs={12} md={7.2} sx={{ width: '60%' }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Integrations</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Connect DymenStock with your favorite tools and services
              </Typography>
              
              {integrationsLoading ? (
                <Box display="flex" justifyContent="center" my={3}>
                  <CircularProgress />
                </Box>
              ) : (
                <Grid container spacing={3}>
                  {/* Twilio Integration */}
                  <Grid item xs={12}>
                    <Paper sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box>
                          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            Twilio SMS
                            <Chip 
                              label={integrations.twilio.configured ? 'Configured' : 'Not Configured'} 
                              size="small" 
                              color={integrations.twilio.configured ? 'success' : 'default'}
                              variant="outlined"
                            />
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Send SMS notifications for orders, deliveries, and alerts
                          </Typography>
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={integrations.twilio.enabled}
                              onChange={() => handleIntegrationToggle('twilio')}
                              disabled={integrationsLoading}
                            />
                          }
                          label="Enable"
                        />
                      </Box>
                      
                      <Divider sx={{ my: 2 }} />
                      
                      <Typography variant="subtitle1" gutterBottom>Configuration</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Account SID"
                            value={twilioConfig.accountSid}
                            onChange={(e) => setTwilioConfig(prev => ({ ...prev, accountSid: e.target.value }))}
                            margin="normal"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Auth Token"
                            type="password"
                            value={twilioConfig.authToken}
                            onChange={(e) => setTwilioConfig(prev => ({ ...prev, authToken: e.target.value }))}
                            margin="normal"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Phone Number"
                            value={twilioConfig.phoneNumber}
                            onChange={(e) => setTwilioConfig(prev => ({ ...prev, phoneNumber: e.target.value }))}
                            margin="normal"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleConfigureIntegration('twilio', twilioConfig)}
                            disabled={integrationsLoading}
                          >
                            Save Configuration
                          </Button>
                          <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => handleTestIntegration('twilio')}
                            disabled={integrationsLoading || !integrations.twilio.configured}
                            sx={{ ml: 2 }}
                          >
                            Test Connection
                          </Button>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* Mailchimp Integration */}
                  <Grid item xs={12}>
                    <Paper sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box>
                          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            Mailchimp
                            <Chip 
                              label={integrations.mailchimp.configured ? 'Configured' : 'Not Configured'} 
                              size="small" 
                              color={integrations.mailchimp.configured ? 'success' : 'default'}
                              variant="outlined"
                            />
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Sync customers and send marketing campaigns
                          </Typography>
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={integrations.mailchimp.enabled}
                              onChange={() => handleIntegrationToggle('mailchimp')}
                              disabled={integrationsLoading}
                            />
                          }
                          label="Enable"
                        />
                      </Box>
                      
                      <Divider sx={{ my: 2 }} />
                      
                      <Typography variant="subtitle1" gutterBottom>Configuration</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="API Key"
                            type="password"
                            value={mailchimpConfig.apiKey}
                            onChange={(e) => setMailchimpConfig(prev => ({ ...prev, apiKey: e.target.value }))}
                            margin="normal"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Server Prefix"
                            value={mailchimpConfig.serverPrefix}
                            onChange={(e) => setMailchimpConfig(prev => ({ ...prev, serverPrefix: e.target.value }))}
                            margin="normal"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="List ID"
                            value={mailchimpConfig.listId}
                            onChange={(e) => setMailchimpConfig(prev => ({ ...prev, listId: e.target.value }))}
                            margin="normal"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleConfigureIntegration('mailchimp', mailchimpConfig)}
                            disabled={integrationsLoading}
                          >
                            Save Configuration
                          </Button>
                          <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => handleTestIntegration('mailchimp')}
                            disabled={integrationsLoading || !integrations.mailchimp.configured}
                            sx={{ ml: 2 }}
                          >
                            Test Connection
                          </Button>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* Gmail Integration */}
                  <Grid item xs={12}>
                    <Paper sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box>
                          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            Gmail (Invoice Email)
                            <Chip
                              label={integrations.gmail?.configured ? 'Configured' : 'Not Configured'}
                              size="small"
                              color={integrations.gmail?.configured ? 'success' : 'default'}
                              variant="outlined"
                            />
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Send invoice emails directly from your Gmail account using an App Password
                          </Typography>
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={integrations.gmail?.enabled || false}
                              onChange={() => handleIntegrationToggle('gmail')}
                              disabled={integrationsLoading}
                            />
                          }
                          label="Enable"
                        />
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      <Typography variant="subtitle1" gutterBottom>Configuration</Typography>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Use a <strong>Gmail App Password</strong>, not your regular password.
                        Go to <strong>Google Account → Security → 2-Step Verification → App passwords</strong> and create one for "Mail".
                      </Alert>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Gmail Address"
                            type="email"
                            value={gmailConfig.user}
                            onChange={(e) => setGmailConfig(prev => ({ ...prev, user: e.target.value }))}
                            placeholder="yourname@gmail.com"
                            margin="normal"
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="App Password"
                            type="password"
                            value={gmailConfig.appPassword}
                            onChange={(e) => setGmailConfig(prev => ({ ...prev, appPassword: e.target.value }))}
                            placeholder="xxxx xxxx xxxx xxxx"
                            margin="normal"
                            helperText="16-character App Password from Google Account"
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="From Name (optional)"
                            value={gmailConfig.fromName}
                            onChange={(e) => setGmailConfig(prev => ({ ...prev, fromName: e.target.value }))}
                            placeholder="Your Business Name"
                            margin="normal"
                            helperText="Name customers see in their inbox"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleConfigureIntegration('gmail', gmailConfig)}
                            disabled={integrationsLoading || !gmailConfig.user || !gmailConfig.appPassword}
                          >
                            Save Configuration
                          </Button>
                          <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => handleTestIntegration('gmail')}
                            disabled={integrationsLoading || !integrations.gmail?.configured}
                            sx={{ ml: 2 }}
                          >
                            Test Connection
                          </Button>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* External Product Catalogs Section */}
                  <Grid item xs={12}>
                    <Paper sx={{ p: 3, mt: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        External Product Catalogs
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Manage external product catalogs like Ashley Furniture. Products from these catalogs are available for ordering but don't appear in your main inventory until explicitly added.
                      </Typography>

                      {catalogsLoading ? (
                        <Box display="flex" justifyContent="center" my={3}>
                          <CircularProgress />
                        </Box>
                      ) : (
                        <>
                          {/* Existing Catalogs */}
                          {externalCatalogs.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="subtitle1" gutterBottom>
                                Connected Catalogs
                              </Typography>
                              {externalCatalogs.map((catalog) => (
                                <Paper key={catalog.id} sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box>
                                      <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {catalog.name}
                                        <Chip 
                                          label={catalog.isActive ? 'Active' : 'Inactive'} 
                                          size="small" 
                                          color={catalog.isActive ? 'success' : 'default'}
                                          variant="outlined"
                                        />
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        {catalog.description || 'No description'}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        URL: {catalog.baseUrl}
                                      </Typography>
                                      {catalog.lastSyncAt && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                          Last synced: {new Date(catalog.lastSyncAt).toLocaleString()}
                                        </Typography>
                                      )}
                                      {catalog.productCount !== undefined && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                          Products: {catalog.productCount}
                                        </Typography>
                                      )}
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => handleSyncCatalog(catalog.id)}
                                        disabled={catalogsLoading}
                                      >
                                        Sync
                                      </Button>
                                      <IconButton
                                        color="error"
                                        size="small"
                                        onClick={() => handleDeleteCatalog(catalog.id)}
                                        disabled={catalogsLoading}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Box>
                                  </Box>
                                </Paper>
                              ))}
                            </Box>
                          )}

                          {/* Add New Catalog */}
                          <Divider sx={{ my: 3 }} />
                          <Typography variant="subtitle1" gutterBottom>
                            Add New External Catalog
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                label="Catalog Name"
                                value={newCatalogName}
                                onChange={(e) => setNewCatalogName(e.target.value)}
                                placeholder="e.g., Ashley Furniture"
                                margin="normal"
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                label="Base URL"
                                value={newCatalogUrl}
                                onChange={(e) => setNewCatalogUrl(e.target.value)}
                                placeholder="https://www.ashleyfurniture.com"
                                margin="normal"
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label="Description (Optional)"
                                value={newCatalogDescription}
                                onChange={(e) => setNewCatalogDescription(e.target.value)}
                                placeholder="External furniture catalog for ordering"
                                margin="normal"
                                multiline
                                rows={2}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Button
                                variant="contained"
                                color="primary"
                                onClick={handleAddCatalog}
                                disabled={catalogsLoading || !newCatalogName.trim() || !newCatalogUrl.trim()}
                                startIcon={catalogsLoading ? <CircularProgress size={20} /> : <Add />}
                              >
                                Add Catalog
                              </Button>
                            </Grid>
                          </Grid>
                        </>
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Appearance Tab */}
      {activeTab === 2 && (
        <Grid container spacing={4}>
          <Grid item xs={12} md={7.2} sx={{ width: '60%' }}>
            <Paper sx={{ p: 3 }}>
              <ThemeSettings />
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Billing Tab */}
      {activeTab === 3 && (
        <Grid container spacing={4}>
          <Grid item xs={12} md={7.2} sx={{ width: '60%' }}>
            <Paper sx={{ p: 3 }}>
              <BillingSettings />
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Pricing Tab */}
      {activeTab === 4 && (
        <Grid container spacing={4}>
          <Grid item xs={12} md={7.2} sx={{ width: '60%' }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Per-Location Pricing</Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="pricing-location-select-label">Location</InputLabel>
                <Select
                  labelId="pricing-location-select-label"
                  value={selectedLocationId}
                  label="Location"
                  onChange={(e) => setSelectedLocationId(e.target.value)}
                >
                  {locations && locations.map(loc => (
                    <MenuItem key={loc._id || loc.id} value={loc._id || loc.id}>
                      {loc.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {pricingLoading ? (
                <Box display="flex" justifyContent="center" my={3}><CircularProgress /></Box>
              ) : (
                <>
                  <Typography variant="subtitle1" gutterBottom>Tax</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: 1, mb: 2 }}>
                    <TextField
                      size="small"
                      type="number"
                      label="Tax Rate (%)"
                      value={taxRate}
                      onChange={(e) => setTaxRate(e.target.value)}
                      inputProps={{ step: '0.01', min: '0', max: '100' }}
                    />
                  </Box>

                  <Typography variant="subtitle1" gutterBottom>Shipping</Typography>
                  {shipping.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>No shipping costs added</Typography>
                  )}
                  {shipping.map((it, idx) => (
                    <Box key={`ship-${idx}`} sx={{ display: 'grid', gridTemplateColumns: '1fr 160px 40px', gap: 1, mb: 1 }}>
                      <TextField size="small" placeholder="Title" value={it.title || ''} onChange={(e) => updateItem('shipping', idx, 'title', e.target.value)} />
                      <TextField size="small" type="number" placeholder="Cost" value={it.cost} onChange={(e) => updateItem('shipping', idx, 'cost', e.target.value)} inputProps={{ step: '0.01', min: '0' }} />
                      <IconButton size="small" onClick={() => removeItem('shipping', idx)}><DeleteIcon fontSize="small" /></IconButton>
                    </Box>
                  ))}
                  <Button size="small" startIcon={<Add />} variant="outlined" onClick={() => addItem('shipping')} sx={{ mb: 2 }}>Add Shipping</Button>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle1" gutterBottom>Warranties</Typography>
                  {warranties.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>No warranties added</Typography>
                  )}
                  {warranties.map((it, idx) => (
                    <Box key={`war-${idx}`} sx={{ display: 'grid', gridTemplateColumns: '1fr 160px 40px', gap: 1, mb: 1 }}>
                      <TextField size="small" placeholder="Title" value={it.title || ''} onChange={(e) => updateItem('warranties', idx, 'title', e.target.value)} />
                      <TextField size="small" type="number" placeholder="Cost" value={it.cost} onChange={(e) => updateItem('warranties', idx, 'cost', e.target.value)} inputProps={{ step: '0.01', min: '0' }} />
                      <IconButton size="small" onClick={() => removeItem('warranties', idx)}><DeleteIcon fontSize="small" /></IconButton>
                    </Box>
                  ))}
                  <Button size="small" startIcon={<Add />} variant="outlined" onClick={() => addItem('warranties')} sx={{ mb: 2 }}>Add Warranty</Button>

                  <Box sx={{ mt: 2 }}>
                    <Button variant="contained" disabled={!selectedLocationId || pricingLoading} onClick={savePricing}>Save</Button>
                  </Box>
                </>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

    </Box>
  );
};

export default SettingsPage;
